import { PageId } from 'types/idTypes';
import { HistoryRecord } from './history.types';

/**
 * Returns the last valid page id based on the current active page id.
 * If no page is found, will return first page in current page order.
 *
 * @param currentPageOrder
 * @param prevPageOrder
 * @param activePageId
 * @returns PageId of the last valid page
 */
const getLastValidPageId = (
  currentPageOrder: PageId[],
  prevPageOrder: PageId[] | undefined,
  activePageId: PageId,
): PageId => {
  const defaultPageId = currentPageOrder[0];
  if (!prevPageOrder) return defaultPageId;

  let newActivePageId = defaultPageId;
  const prevIdx = prevPageOrder.findIndex((id: string) => id === activePageId) || 0;
  if (prevIdx === -1) return newActivePageId;

  const numPagesRemoved = prevPageOrder.length - currentPageOrder.length;

  // Case 1: Single page was removed by undo (i.e. add page, single page template, duplicate)
  // Return the page before the removed page
  if (numPagesRemoved === 1) {
    newActivePageId = currentPageOrder[prevIdx - 1] || defaultPageId;

    // Case 2: Multiple pages were removed by undo (i.e. add all pages from multipage template)
    // Return the first page that exists in the previous page order
  } else if (numPagesRemoved > 1) {
    let currIdx = prevIdx - 1;
    while (currIdx >= 0) {
      const prevId = prevPageOrder[currIdx];
      const pageId = currentPageOrder.find((id: string) => id === prevId);
      if (pageId) {
        newActivePageId = pageId;
        break;
      }
      currIdx -= 1;
    }
  }

  return newActivePageId || defaultPageId;
};

/**
 * Returns the related page id or widget id from the inverse patch of a history record
 *
 * @param record - A history record
 * @returns { pageId: string | undefined, widgetId: string | undefined }
 */
const getRecordPathId = (record: HistoryRecord) => {
  const inversePatches = record?.inversePatches;

  let pageId;
  let widgetId;

  if (inversePatches && inversePatches.length > 0) {
    for (let i = 0; i < inversePatches.length; i += 1) {
      const path = inversePatches[i]?.path;
      const key = path?.[0];

      // id is always the second index in the path array
      if (key === 'pages') {
        pageId = path[1] as string;
        break;
      }

      if (key === 'widgets') {
        widgetId = path[1] as string;
        break;
      }
    }
  }

  return { pageId, widgetId };
};

export { getLastValidPageId, getRecordPathId };
